import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { IQueryData, IGlassesPackage, IFeature, IAddOn } from '../../types';
import { FETCH_APP_DATA } from '../../graphql';
import { Container } from '../Container';
import { BackgroundColorWrapper } from '../BackgroundColorWrapper';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { GlassesPackageView } from '../GlassesPackageView';
import { GlassesPackageSelector } from '../GlassesPackageSelector';
import { GlassesTypeSelector } from '../GlassesTypeSelector';
import { getQSVar } from '../../utils';

export const Ssp = () => {
    const [showMultifocal, setShowMultifocal] = useState(
        getQSVar('template') === 'multifocal'
    );
    const [displayedPackages, setDisplayedPackages] = useState<
        IGlassesPackage[] | undefined
    >([]);
    const [selectedPackage, setSelectedPackage] = useState<
        IGlassesPackage | undefined
    >();
    const [displayedFeatures, setDisplayedFeatures] = useState<
        IFeature[] | undefined
    >([]);
    const [displayedAddOns, setDisplayedAddOns] = useState<IAddOn[]>([]);
    const [selectedAddOns, setSelectedAddOns] = useState<IAddOn[]>([]);
    const { data } = useQuery<IQueryData>(FETCH_APP_DATA, {
        variables: {
            versionStatus: process.env.REACT_APP_VERSION_STATUS ?? null,
        },
    });

    // Filter and set available packages and features based on multifocal setting
    useEffect(() => {
        if (!data) return;

        const displayedGlassesPackages = data.glassesPackages
            .filter(
                (glassesPackage) => glassesPackage.multifocal === showMultifocal
            )
            .sort((a, b) => a.order - b.order);

        const displayedPackageFeatures = data.features
            .filter((feature) => feature.multifocalOnly === showMultifocal)
            .sort((a, b) => a.order - b.order);

        setDisplayedPackages(displayedGlassesPackages);
        setDisplayedFeatures(displayedPackageFeatures);
    }, [data, showMultifocal]);

    // Set selected package when packages are loaded or when multifocal settings change
    useEffect(() => {
        if (!displayedPackages) return;

        const newSelectedPackage =
            displayedPackages.find((glassesPackage) => {
                // Find package with same name when switching multifocal setting
                if (selectedPackage) {
                    return selectedPackage.name === glassesPackage.name;
                }

                return glassesPackage.default;
            }) || displayedPackages[1];

        setSelectedPackage(newSelectedPackage);
    }, [displayedPackages, selectedPackage]);

    // Set diplayed addOns when packages are loaded or when selected package changes
    useEffect(() => {
        if (!selectedPackage) return;

        // Add-on is free if package is included
        const addOns = selectedPackage.addOns.map((addOn) => ({
            ...addOn,
            priceInt: addOn.freeWithPackagesIds.includes(selectedPackage.id)
                ? 0
                : addOn.priceInt,
        }));

        setDisplayedAddOns(addOns);
    }, [selectedPackage]);

    // Set selected addOn when packages are loaded or when selected package changes
    useEffect(() => {
        if (!selectedPackage) return;

        setSelectedAddOns([]);
    }, [selectedPackage]);

    if (!data || !displayedPackages || !selectedPackage || !displayedFeatures) {
        return null;
    }

    const handleAddOnClick = (addOn: IAddOn) => {
        const addOnIsSelected = selectedAddOns
            .map((selectedAddOn) => selectedAddOn.id)
            .includes(addOn.id);

        // Deselect addOn if already selected, keep any other selected addOns
        if (addOnIsSelected) {
            setSelectedAddOns(
                selectedAddOns.filter(
                    (selectedAddOn) => selectedAddOn.id !== addOn.id
                )
            );

            return;
        }

        // In all other cases
        setSelectedAddOns([...selectedAddOns, addOn]);
    };

    return (
        <Container>
            <Header />
            <GlassesTypeSelector
                selectedPackage={selectedPackage}
                setShowMultifocal={setShowMultifocal}
                showMultifocal={showMultifocal}
            />
            <BackgroundColorWrapper
                color={selectedPackage.backgroundColorGeneral}
            >
                <GlassesPackageSelector
                    packages={displayedPackages}
                    selectedPackage={selectedPackage}
                    onPackageClick={setSelectedPackage}
                />
                <GlassesPackageView
                    addOns={displayedAddOns}
                    features={displayedFeatures}
                    selectedAddOns={selectedAddOns}
                    selectedPackage={selectedPackage}
                    onAddOnClick={handleAddOnClick}
                />
                <Footer>
                    {!!selectedPackage.discountPercentage && selectedPackage.discountPercentage > 0 && (
                        <>
                            *{' '}
                            <a
                                href="https://www.pearle.nl/acties/actievoorwaarden"
                                rel="noreferrer"
                                target="_blank"
                            >
                                Bekijk de voorwaarden hier
                            </a>
                            .
                            <br />
                        </>
                    )}
                    Let op: je kunt online een bril bestellen met diverse
                    glaspakketten en glasopties, maar niet alle extra glasopties
                    zijn mogelijk bij een online bestelling. Vragen?{' '}
                    <a
                        href="https://www.pearle.nl/klantenservice"
                        rel="noreferrer"
                        target="_blank"
                    >
                        Neem contact met ons op.
                    </a>
                </Footer>
            </BackgroundColorWrapper>
        </Container>
    );
};
